import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArtworkCard({ artwork, onFavoriteChange }) {
  const { isAuthenticated, getAuthHeaders, isArtist } = useAuth();
  const { addToCart } = useCart();
  const [isFavorited, setIsFavorited] = useState(false);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (addToCart(artwork)) {
      toast.success('Added to cart');
    } else {
      toast.error('Already in cart');
    }
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to add favorites');
      return;
    }

    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${artwork.id}`, getAuthHeaders());
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { artwork_id: artwork.id }, getAuthHeaders());
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
      if (onFavoriteChange) onFavoriteChange();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="group relative overflow-hidden bg-transparent"
      data-testid={`artwork-card-${artwork.id}`}
    >
      <Link to={`/artwork/${artwork.id}`}>
        <div className="aspect-[3/4] overflow-hidden bg-muted">
          <img
            src={artwork.image_url}
            alt={artwork.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid={`artwork-image-${artwork.id}`}
          />
        </div>
      </Link>

      <div className="mt-4 flex justify-between items-start">
        <Link to={`/artwork/${artwork.id}`} className="flex-1">
          <h3 className="font-serif text-xl font-light tracking-tight" data-testid={`artwork-title-${artwork.id}`}>
            {artwork.title}
          </h3>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
            {artwork.category}
          </p>
          <p className="text-lg font-medium mt-2" data-testid={`artwork-price-${artwork.id}`}>
            ${artwork.price.toFixed(2)}
          </p>
        </Link>

        <div className="flex gap-2 ml-4">
          {!isArtist && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleFavorite}
                className="hover:scale-110 transition-transform"
                data-testid={`favorite-btn-${artwork.id}`}
              >
                <Heart className={`h-5 w-5 ${isFavorited ? 'fill-accent text-accent' : ''}`} />
              </Button>
              {artwork.is_available && (
                <Button
                  size="sm"
                  onClick={handleAddToCart}
                  className="rounded-full px-6 uppercase tracking-widest text-xs hover:scale-105 transition-transform"
                  data-testid={`add-to-cart-btn-${artwork.id}`}
                >
                  ADD
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {!artwork.is_available && (
        <div className="mt-2">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground bg-secondary px-3 py-1 rounded-full">
            SOLD
          </span>
        </div>
      )}
    </motion.div>
  );
}